// /dev/render-animation-preview — DEV-ONLY one-shot harness (Jun 13).
//
// Renders an animation template's preview MP4 using the AI template library
// samples (frontend/public/ai-templates/product-shot/*.webp) as input
// (decision #3: "use the template images to generate the MP4 — that should
// sell the animation"). Download the result + drop it into
// frontend/public/animation-templates/previews/<id>.mp4, commit, done.
//
// Why a separate dev page (vs. doing this via /animation): the AI samples
// aren't in My Creatives, so /animation's image picker can't pull them
// directly. This page bypasses that — list of curated AI sample URLs hard-
// coded below, loaded as HTMLImageElements, fed into the same encoder
// /animation uses. Output byte-for-byte matches what users get at runtime
// because the renderer is shared.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ANIM_DIMENSIONS, loadAnimImage, type AnimationSpec, type AnimAspect } from '@/lib/animation/types';
import { encodeAnimationToMp4 } from '@/lib/animation/encode';
import { ANIMATION_TEMPLATES, type AnimationTemplate } from '@/mocks/animation-templates';
import {
  PRODUCT_SHOT_AI_TEMPLATES,
  aiTemplateSamplePath,
  type AiTemplate,
  type AiTemplateDomain,
} from '@/mocks/ai-templates';

type DomainFilter = AiTemplateDomain | 'all';
const DOMAIN_OPTIONS: { id: DomainFilter; label: string }[] = [
  { id: 'skincare', label: 'Skincare' },
  { id: 'wearable', label: 'Wearable' },
  { id: 'consumable', label: 'Consumable' },
  { id: 'all', label: 'All (mixed)' },
];

type CategoryFilter = AiTemplate['category'] | 'all';
const CATEGORY_OPTIONS: { id: CategoryFilter; label: string }[] = [
  { id: 'all',        label: 'All' },
  { id: 'studio',     label: 'Studio' },
  { id: 'model',      label: 'Model / Held' },
  { id: 'ingredient', label: 'Ingredient' },
  { id: 'contextual', label: 'Contextual' },
  { id: 'lifestyle',  label: 'Lifestyle' },
];

const ASPECT_OPTIONS: { id: AnimAspect; label: string }[] = [
  { id: '9:16', label: '9:16 Vertical' },
  { id: '4:5',  label: '4:5 Portrait' },
  { id: '1:1',  label: '1:1 Square' },
];

// Tasteful preset swatches for the bg picker — light neutrals + a couple of
// statement options. User can also type any hex via the input next to them.
const BG_PRESETS: { value: string; label: string }[] = [
  { value: '#EDEDED', label: 'Light gray' },
  { value: '#FFFFFF', label: 'White' },
  { value: '#F5F1EA', label: 'Cream' },
  { value: '#E8DDC8', label: 'Beige' },
  { value: '#FCE7F1', label: 'Soft pink' },
  { value: '#1A1A1A', label: 'Dark' },
  { value: '#000000', label: 'Black' },
  { value: '#E8F0EE', label: 'Sage' },
];

const HEX_RE = /^#?[0-9a-fA-F]{6}$/;

const IS_DEV = import.meta.env.DEV;

export default function RenderAnimationPreviewPage() {
  const [templateId, setTemplateId] = useState<string>(
    ANIMATION_TEMPLATES[0]?.id ?? '',
  );
  // Decision (Jun 13): pick all preview images from ONE domain (skincare /
  // wearable / consumable) so the preview MP4 reads as cohesive — mixed
  // domains (e.g. a sneaker next to a skincare bottle) look like a sampler,
  // not a brand. "All (mixed)" stays as an option for testing only.
  const [domain, setDomain] = useState<DomainFilter>('skincare');
  // Category filter (Jun 13) — pick a single scene category (studio / model /
  // ingredient / contextual / lifestyle) so the preview is visually cohesive
  // (e.g. all "Studio" or all "Model / Held"). Default 'all' = no filter.
  const [category, setCategory] = useState<CategoryFilter>('all');
  // Aspect override — for experimenting with how a template looks at a
  // different output ratio without editing the JSON. Initialised from the
  // selected template; auto-re-syncs whenever the template changes so the
  // default always matches what the template would ship at.
  const [aspect, setAspect] = useState<AnimAspect>(
    ANIMATION_TEMPLATES[0]?.aspectRatio ?? '4:5',
  );
  useEffect(() => {
    const t = ANIMATION_TEMPLATES.find((x) => x.id === templateId);
    if (t) {
      setAspect(t.aspectRatio);
      setBackground(t.backgroundColor ?? '#EDEDED');
    }
  }, [templateId]);

  // Bg color override (Jun 13) — preview-only. Defaults to the selected
  // template's bg (or '#EDEDED' fallback); re-syncs whenever the template
  // changes. Use this to A/B different surfaces before saving a bg into the
  // template JSON (hand-edit animation-templates.ts to ship a different
  // default per template).
  const [background, setBackground] = useState<string>(
    ANIMATION_TEMPLATES[0]?.backgroundColor ?? '#EDEDED',
  );
  const [bgInput, setBgInput] = useState<string>(background);
  // Keep the text input synced when a preset / template swap changes the bg.
  useEffect(() => { setBgInput(background); }, [background]);
  const commitBgInput = useCallback(() => {
    const v = bgInput.trim();
    if (!HEX_RE.test(v)) {
      setBgInput(background); // revert
      return;
    }
    setBackground(v.startsWith('#') ? v.toUpperCase() : `#${v.toUpperCase()}`);
  }, [bgInput, background]);

  // AI templates filtered by selected domain. Source of the preview images
  // — the AI library is the curated set we have; My Creatives would vary
  // per user. Sample path resolves to /ai-templates/product-shot/<id>.webp
  // (the picker-grid thumb; runtime renders use the full-res PNG).
  const sampleTemplates = useMemo<AiTemplate[]>(() => {
    let pool: readonly AiTemplate[] = PRODUCT_SHOT_AI_TEMPLATES;
    if (domain !== 'all') pool = pool.filter((t) => t.domain === domain);
    if (category !== 'all') pool = pool.filter((t) => t.category === category);
    return [...pool];
  }, [domain, category]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Revoke any prior download URL on unmount.
  useEffect(() => () => { if (downloadUrl) URL.revokeObjectURL(downloadUrl); }, [downloadUrl]);

  const run = useCallback(async () => {
    const template = ANIMATION_TEMPLATES.find((t) => t.id === templateId);
    if (!template || running) return;
    if (sampleTemplates.length === 0) {
      setError(`No AI templates tagged with domain="${domain}". Tag some via /create-template (or pick a different domain).`);
      return;
    }
    setRunning(true);
    setProgress(0);
    setError(null);
    setDownloadUrl(null);
    try {
      setStatus(`Loading ${sampleTemplates.length} ${domain === 'all' ? '' : domain + ' '}sample images…`);
      const loadedAll = await Promise.all(
        sampleTemplates.map((t) => loadAnimImage(aiTemplateSamplePath(t))),
      );

      // Aspect auto-grouping (Jun 13): cluster the loaded samples by aspect
      // ratio with a 10% tolerance, then keep only the largest cluster. Mixed
      // aspects in a card-swipe / mosaic look "off" because the cards/tiles
      // come out at different shapes. Users can curate at /create-template;
      // this just enforces consistency at render time.
      type Bucket = { items: HTMLImageElement[]; avgAspect: number };
      const buckets: Bucket[] = [];
      for (const img of loadedAll) {
        const aspect = img.naturalHeight / Math.max(1, img.naturalWidth);
        let placed = false;
        for (const b of buckets) {
          if (Math.abs(aspect - b.avgAspect) / b.avgAspect < 0.1) {
            const next = (b.avgAspect * b.items.length + aspect) / (b.items.length + 1);
            b.items.push(img);
            b.avgAspect = next;
            placed = true;
            break;
          }
        }
        if (!placed) buckets.push({ items: [img], avgAspect: aspect });
      }
      buckets.sort((a, b) => b.items.length - a.items.length);
      const images = buckets[0]?.items ?? loadedAll;
      const droppedCount = loadedAll.length - images.length;

      if (images.length === 0) {
        throw new Error('No images loaded — pick different filters.');
      }
      if (droppedCount > 0) {
        const ratio = buckets[0].avgAspect;
        const label = ratio > 1.5 ? '9:16' : ratio > 1.15 ? '4:5' : ratio < 0.7 ? '16:9' : '1:1';
        setStatus(`Filtered to ${images.length} images (${label} aspect) — dropped ${droppedCount} mismatched.`);
      }
      setStatus((s) => `${s ?? `Using ${images.length} images.`} Encoding MP4…`);
      const { w, h } = ANIM_DIMENSIONS[aspect];
      const spec: AnimationSpec = {
        images,
        motionStyle: template.motionStyle,
        motionConfig: template.motionConfig,
        backgroundColor: background,
        perImageMs: template.perImageMs ?? 2500,
        transitionMs: 700,
      };
      const blob = await encodeAnimationToMp4({
        spec,
        width: w,
        height: h,
        onProgress: setProgress,
      });
      const url = URL.createObjectURL(blob);
      setDownloadUrl(url);
      setStatus(`Done — ${(blob.size / 1024 / 1024).toFixed(2)} MB. Save the file as ${template.id}.mp4 and drop it into frontend/public/animation-templates/previews/.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Render failed');
      setStatus(null);
    } finally {
      setRunning(false);
    }
  }, [templateId, running, sampleTemplates, domain, aspect, background]);

  if (!IS_DEV) {
    return (
      <div className="min-h-screen flex items-center justify-center text-dark-muted text-sm">
        Animation preview renderer is a dev-only tool — run it on the local dev server.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface px-6 py-8">
      <div className="max-w-[800px] mx-auto">
        <h1 className="font-display font-bold text-dark text-2xl mb-2 flex items-center gap-2">
          <i className="ri-film-line text-primary" />
          Render Animation Preview
          <span className="px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[11px] font-bold uppercase tracking-wide">
            dev
          </span>
        </h1>
        <p className="text-dark-muted text-sm mb-6 max-w-prose">
          One-shot tool: render a template's preview MP4 using the AI template
          library samples as input. Download the result + drop it into
          <code className="mx-1 px-1.5 py-0.5 rounded bg-white border border-surface-dark/30 text-[12px]">
            frontend/public/animation-templates/previews/{templateId || '<id>'}.mp4
          </code>
          (filename = template id + .mp4).
        </p>

        <div className="bg-white rounded-2xl border-2 border-surface-dark/30 p-5 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-dark-muted mb-1.5">
              Template
            </label>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              disabled={running}
              className="w-full px-3 py-2 rounded-lg border border-dark-muted/40 bg-white text-sm"
            >
              {ANIMATION_TEMPLATES.map((t: AnimationTemplate) => (
                <option key={t.id} value={t.id}>
                  {t.name} — {t.id} ({t.aspectRatio}, {t.durationSec}s)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-dark-muted mb-1.5">
              Image domain
            </label>
            <div className="flex flex-wrap gap-1.5">
              {DOMAIN_OPTIONS.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setDomain(d.id)}
                  disabled={running}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer disabled:opacity-50 ${
                    domain === d.id
                      ? 'bg-primary text-white'
                      : 'bg-white border border-dark-muted/40 text-dark hover:border-primary/40'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-dark-muted mt-1.5">
              {sampleTemplates.length} AI templates match — preview MP4 will use all of them.
              {sampleTemplates.length === 0 && (
                <span className="text-red-700 font-bold"> Tag some via /create-template or pick a different filter.</span>
              )}
            </p>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-dark-muted mb-1.5">
              Source category
            </label>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORY_OPTIONS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategory(c.id)}
                  disabled={running}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer disabled:opacity-50 ${
                    category === c.id
                      ? 'bg-primary text-white'
                      : 'bg-white border border-dark-muted/40 text-dark hover:border-primary/40'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-dark-muted mt-1.5">
              Filter by scene category (set when you save in /create-template).
            </p>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-dark-muted mb-1.5">
              Aspect ratio
            </label>
            <div className="flex flex-wrap gap-1.5">
              {ASPECT_OPTIONS.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setAspect(a.id)}
                  disabled={running}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer disabled:opacity-50 ${
                    aspect === a.id
                      ? 'bg-primary text-white'
                      : 'bg-white border border-dark-muted/40 text-dark hover:border-primary/40'
                  }`}
                >
                  {a.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-dark-muted mt-1.5">
              {ANIM_DIMENSIONS[aspect].w}×{ANIM_DIMENSIONS[aspect].h}
              {(() => {
                const t = ANIMATION_TEMPLATES.find((x) => x.id === templateId);
                return t && aspect !== t.aspectRatio
                  ? ` · overrides template default (${t.aspectRatio})`
                  : ' · template default';
              })()}
            </p>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-dark-muted mb-1.5">
              Background color
            </label>
            <div className="flex flex-wrap items-center gap-2">
              {BG_PRESETS.map((p) => {
                const active = background.toUpperCase() === p.value.toUpperCase();
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setBackground(p.value)}
                    disabled={running}
                    title={`${p.label} · ${p.value}`}
                    aria-label={p.label}
                    className={`w-8 h-8 rounded-lg border-2 transition-all cursor-pointer disabled:opacity-50 ${
                      active ? 'border-primary scale-110' : 'border-surface-dark/40 hover:border-primary/40'
                    }`}
                    style={{ backgroundColor: p.value }}
                  />
                );
              })}
              <span className="mx-1 text-dark-muted/50 text-xs">|</span>
              <div className="flex items-center gap-1.5">
                {/* Live preview swatch + hex input. Native color picker
                    (input type="color") gives an OS-native palette, and the
                    text input lets you paste any hex. Both stay in sync. */}
                <input
                  type="color"
                  value={background}
                  onChange={(e) => setBackground(e.target.value.toUpperCase())}
                  disabled={running}
                  title="Pick any color"
                  className="w-8 h-8 rounded-lg border border-surface-dark/40 cursor-pointer disabled:opacity-50"
                />
                <input
                  type="text"
                  value={bgInput}
                  onChange={(e) => setBgInput(e.target.value)}
                  onBlur={commitBgInput}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitBgInput(); }}
                  disabled={running}
                  placeholder="#EDEDED"
                  className="w-24 px-2 py-1 rounded-lg border border-dark-muted/40 bg-white text-xs font-mono uppercase disabled:opacity-50"
                />
              </div>
            </div>
            <p className="text-[11px] text-dark-muted mt-1.5">
              Only visible for motion styles that expose the canvas
              (mosaic-scroll, card-swipe). Previewing only — to ship a default
              bg with the template, hand-edit
              <code className="mx-1 px-1 py-0.5 rounded bg-surface text-[10px]">animation-templates.ts</code>.
            </p>
          </div>

          <button
            type="button"
            onClick={run}
            disabled={running || !templateId}
            className={`
              w-full inline-flex items-center justify-center gap-2
              px-6 py-3 rounded-full text-sm font-bold transition-all cursor-pointer
              ${running || !templateId
                ? 'bg-surface-dark/30 text-dark-muted cursor-not-allowed'
                : 'bg-primary hover:bg-primary-hover text-white shadow-md shadow-primary/25'}
            `}
          >
            {running
              ? <><i className="ri-loader-4-line animate-spin" /> {status ?? 'Working…'}</>
              : <><i className="ri-play-line" /> Render preview MP4</>}
          </button>

          {running && (
            <div className="space-y-1">
              <div className="h-2 bg-surface rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-200"
                  style={{ width: `${Math.round(progress * 100)}%` }}
                />
              </div>
              <p className="text-[11px] text-dark-muted text-right">{Math.round(progress * 100)}%</p>
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {downloadUrl && !running && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-2">
              <p className="text-sm font-bold text-emerald-800 flex items-center gap-1.5">
                <i className="ri-check-line" />
                Render done — preview it below, then save.
              </p>
              <video
                src={downloadUrl}
                className="w-full max-w-[300px] rounded-lg border border-emerald-300"
                autoPlay
                muted
                loop
                playsInline
              />
              <a
                href={downloadUrl}
                download={`${templateId}.mp4`}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-colors cursor-pointer"
              >
                <i className="ri-download-line" />
                Download {templateId}.mp4
              </a>
              <p className="text-[11px] text-emerald-700">
                Then drop it into <code className="px-1 py-0.5 rounded bg-emerald-100">frontend/public/animation-templates/previews/</code> and refresh /templates.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
